/**
 * Op capture + outbox rebase engine (Collab Phase 8).
 *
 * Owns the client's authoritative-order reconciliation: it installs the store's
 * op-capture sink, tracks the pending outbox and `lastAppliedSeq`, and applies
 * every server frame through the corrected rebase contract:
 *
 *  - LOCAL mutation → sink fires → classify (op-transform) → push to outbox →
 *    send with baseSeq = lastAppliedSeq.
 *  - ACK (our clientOpId echoed back) → drop the oldest pending, advance seq.
 *  - REMOTE op that DOESN'T overlap any pending path → apply directly.
 *  - REMOTE op that OVERLAPS pending → roll back ALL pending newest→oldest
 *    (apply their reverses), apply the remote op, re-derive each pending by
 *    instanceId against the converged state, re-apply oldest→newest. NEVER
 *    "apply remote then re-apply pending on top" — pending ops are already IN
 *    local state, so a naive re-apply double-applies insert/remove/move.
 *  - op-rejected → roll back newer pending first, then the rejected op, then:
 *    lock_held → drop+toast, stale → re-derive + resubmit (new clientOpId),
 *    apply_failed/not_seeded → resync.
 *
 * Remote/rollback/re-apply all route through `store.applyRemoteOps` (the
 * sanctioned no-history, epoch-bumping path) so re-applications never re-emit
 * to the sink. Reverses of re-applied ops are recomputed against live state so
 * a subsequent rollback restores the CONVERGED base, not the stale one.
 *
 * Pure-ish + injectable (store, send, randomId, callbacks) for the fake-socket
 * tests. Imports only patch-utils (worker-safe apply/getByPath) and op-transform
 * (zero-import geometry).
 */

import { applyPatches, getByPath } from '../shared/patch-utils.js';
import { deriveOpMetadata, patchesIntersect, rederivePatchesByInstance } from './op-transform.js';
import { isEditableIdRepairAbsentPath } from '../shared/collab-protocol.js';

/** Beyond this many unacked ops the outbox is desynced — snapshot-resync. */
const OFFLINE_OP_CAP = 200;
const MAX_STALE_RESUBMITS = 5;

const SECTIONS_ARRAY_RE = /^pages\.\d+\.sections$/;

/**
 * A patch that changes array membership/order at a structural boundary (the
 * pages array or a page's sections array). Concurrent structural ops can't be
 * rebased in place without reproducing the server's exact re-ordering, so an
 * overlap involving one resyncs (adopt the authoritative order) instead.
 */
function patchesAreStructural(patches) {
  return (patches || []).some(
    (p) =>
      p.path?.split('.').includes('editableIdRepairs') ||
      ((p.op === 'insert' || p.op === 'remove' || p.op === 'move') &&
        (p.path === 'pages' || SECTIONS_ARRAY_RE.test(p.path)))
  );
}

function invertOne(state, patch) {
  switch (patch.op) {
    case 'replace':
      return {
        op: 'replace',
        path: patch.path,
        value: structuredClone(getByPath(state, patch.path) ?? null)
      };
    case 'insert':
      return { op: 'remove', path: patch.path, index: patch.index };
    case 'remove': {
      const arr = getByPath(state, patch.path);
      return {
        op: 'insert',
        path: patch.path,
        index: patch.index,
        item: structuredClone(arr?.[patch.index] ?? null)
      };
    }
    case 'move':
      return { op: 'move', path: patch.path, from: patch.to, to: patch.from };
    default:
      return { op: 'replace', path: patch.path, value: null };
  }
}

export function createOpCapture({
  store,
  send,
  randomId = () => crypto.randomUUID(),
  onResyncNeeded = () => {},
  onOpDropped = () => {},
  getIdentityRepairContext = () => null
} = {}) {
  /** @type {Array<{clientOpId,baseSeq,forward,reverse,sections,structural,pageId,label,staleTries}>} */
  let pending = [];
  let lastAppliedSeq = 0;
  let installed = false;

  function apply(patches, meta) {
    return store.applyRemoteOps(patches, meta || {});
  }

  /** Reverse of `patches` relative to CURRENT live state (before applying). */
  function reverseOf(patches) {
    const clone = structuredClone(store.getState());
    const reverse = [];
    for (const patch of patches) {
      reverse.unshift(invertOne(clone, patch));
      applyPatches(clone, [patch]);
    }
    return reverse;
  }

  function pendingPatches() {
    const out = [];
    for (const op of pending) out.push(...op.forward);
    return out;
  }

  function sendOp(op) {
    const index = pending.indexOf(op);
    // Keep subsequent edits behind an identity transition until its ack. An
    // async server digest must not race a later optimistic write from us.
    if (pending.slice(0, Math.max(0, index)).some((entry) => entry.identityRepair)) return false;
    if (op.identityRepair) {
      if (index !== 0) return false;
      const context = getIdentityRepairContext();
      if (
        !context?.ok ||
        context.projectId !== op.projectId ||
        context.roomEpoch !== op.roomEpoch
      ) {
        onResyncNeeded('identity-repair-session-changed');
        return false;
      }
    }
    send({
      t: 'op',
      clientOpId: op.clientOpId,
      baseSeq: op.baseSeq,
      forward: op.forward,
      label: op.label,
      sections: op.sections,
      structural: op.structural,
      pageId: op.pageId,
      ...(op.identityRepair ? { identityRepair: 1, roomEpoch: op.roomEpoch } : {})
    });
  }

  /**
   * The state `forward` was authored against. The sink fires AFTER the local
   * mutation landed, but deriveOpMetadata's array-patch simulation (16A2a)
   * must run on the PRE-op state — the server derives before applying, and a
   * remove at index i resolves to a different section after the splice. Only
   * structural ops pay the clone; replace-only ops derive identically either
   * side of the mutation and keep the cheap path.
   */
  function derivationBasis(forward, reverse) {
    if (!patchesAreStructural(forward)) return store.getState();
    const pre = structuredClone(store.getState());
    applyPatches(pre, reverse || []);
    return pre;
  }

  function buildOp(forward, reverse, label, staleTries = 0) {
    const meta = deriveOpMetadata(derivationBasis(forward, reverse), forward);
    if (!meta) return null;
    const context = meta.identityRepair ? getIdentityRepairContext() : null;
    if (meta.identityRepair && (!context?.ok || pending.length > 0)) return null;
    const wireForward = meta.identityRepair
      ? forward.map((patch) =>
          patch.op === 'replace' &&
          patch.value === undefined &&
          isEditableIdRepairAbsentPath(patch.path)
            ? { ...patch, value: null, absent: true }
            : patch
        )
      : forward;
    return {
      clientOpId: randomId(),
      baseSeq: lastAppliedSeq,
      forward: wireForward,
      reverse: reverse || [],
      sections: meta.sections,
      structural: meta.structural,
      pageId: meta.pageId,
      label: label ?? null,
      staleTries,
      ...(meta.identityRepair
        ? { identityRepair: 1, projectId: context.projectId, roomEpoch: context.roomEpoch }
        : {})
    };
  }

  // ── sink: a locally-committed mutation ──
  function onLocalOp(entry) {
    const forward = entry?.forward;
    if (!Array.isArray(forward) || forward.length === 0) return;
    const op = buildOp(forward, entry.reverse, entry.label);
    if (!op) {
      // Page-spanning / unresolvable: the server would reject it — the only
      // safe recovery is to re-sync local state to the room head.
      onResyncNeeded('unclassifiable-op');
      return;
    }
    pending.push(op);
    sendOp(op);
    if (pending.length > OFFLINE_OP_CAP) onResyncNeeded('offline-cap');
  }

  // ── server op frame (ack OR peer op) ──
  function handleServerOp(frame) {
    if (frame.clientOpId != null) {
      // Ack of one of OUR ops. Pipelined in submission order → must be oldest.
      if (!pending.length || pending[0].clientOpId !== frame.clientOpId) {
        onResyncNeeded('echo-mismatch');
        return;
      }
      const acknowledged = pending.shift();
      lastAppliedSeq = frame.seq;
      if (acknowledged.identityRepair) {
        for (const op of pending) op.baseSeq = lastAppliedSeq;
        flushPending();
      }
      return;
    }
    if (frame.seq <= lastAppliedSeq) return; // duplicate broadcast
    if (frame.seq !== lastAppliedSeq + 1) {
      onResyncNeeded('seq-gap');
      return;
    }
    applyRemoteFrame(frame);
  }

  function applyRemoteFrame(frame) {
    const meta = {
      authorId: frame.authorSub ?? frame.authorConnId ?? null,
      label: frame.label ?? null,
      seq: frame.seq
    };
    if (!pending.length || !patchesIntersect(frame.forward, pendingPatches())) {
      apply(frame.forward, meta);
      lastAppliedSeq = frame.seq;
      store.trimHistoryForRemote?.(frame.forward);
      return;
    }
    // A structural op on either side (this remote op OR any pending op) can't be
    // rebased in place — local re-derivation would pick an array order that
    // diverges from the server's total order, and subsequent index-based ops
    // would then apply to the wrong element. Resync adopts the authoritative
    // ordering. Replace-vs-replace overlaps rebase cleanly below.
    if (patchesAreStructural(frame.forward) || pending.some((p) => p.structural)) {
      onResyncNeeded('structural-overlap');
      return;
    }
    // Overlap → rollback-all, apply remote, re-derive + re-apply.
    const preRollback = structuredClone(store.getState());
    for (let i = pending.length - 1; i >= 0; i -= 1) {
      apply(pending[i].reverse, { label: 'collab rebase rollback', seq: frame.seq });
    }
    apply(frame.forward, meta);
    lastAppliedSeq = frame.seq;
    store.trimHistoryForRemote?.(frame.forward);
    pending = reapply(pending, preRollback);
  }

  /**
   * Re-derive each op in `ops` (oldest→newest) against the converged live state
   * and re-apply it, recomputing its reverse. Ops whose target vanished are
   * dropped (caller-visible via onOpDropped). Returns the surviving ops. Does
   * NOT resend — these ops are already on the wire; the server applies the
   * originals it received (index-stable → identical; index-shifted → the
   * structural-staleness gate rejects them and the stale path resubmits).
   */
  function rewindOptimisticState(optimisticState, ops) {
    const cursor = structuredClone(optimisticState);
    for (let i = ops.length - 1; i >= 0; i -= 1) {
      applyPatches(cursor, ops[i].reverse);
    }
    return cursor;
  }

  function reapply(ops, optimisticState) {
    const survivors = [];
    // `optimisticState` is the state AFTER every op in this batch. Rewind a
    // private cursor first, then advance it with each ORIGINAL op as we walk
    // oldest→newest. Structural patches are index-based, so using the final
    // optimistic state as every op's old basis can retarget a remove/move to
    // the wrong instance after snapshot adoption.
    const localCursor = rewindOptimisticState(optimisticState, ops);
    for (const op of ops) {
      if (op.identityRepair) {
        applyPatches(localCursor, op.forward);
        onOpDropped(op, { reason: 'identity-repair-needs-replan' });
        continue;
      }
      const rederived = rederivePatchesByInstance(op.forward, localCursor, store.getState());
      // Later local ops were authored against the result of this original op,
      // even when its target vanished from the new authoritative state.
      applyPatches(localCursor, op.forward);
      if (!rederived) {
        onOpDropped(op, { reason: 'vanished' });
        continue;
      }
      const reverse = reverseOf(rederived);
      apply(rederived, { label: op.label, seq: lastAppliedSeq });
      survivors.push({ ...op, forward: rederived, reverse, baseSeq: lastAppliedSeq });
    }
    return survivors;
  }

  // ── op-rejected ──
  function handleRejected(frame) {
    const idx = pending.findIndex((p) => p.clientOpId === frame.clientOpId);
    if (idx < 0) return; // already reconciled
    const op = pending[idx];
    const rejectedAndNewer = pending.slice(idx);
    const newer = pending.slice(idx + 1);
    const preRollback = structuredClone(store.getState());
    // Roll back the rejected op AND everything newer, newest→oldest.
    for (let i = pending.length - 1; i >= idx; i -= 1) {
      apply(pending[i].reverse, { label: 'collab reject rollback', seq: lastAppliedSeq });
    }
    // The rejected local edit is gone from the room's future — retire any local
    // undo entry that touches its paths.
    store.trimHistoryForRemote?.(op.forward);
    pending = pending.slice(0, idx);

    switch (frame.reason) {
      case 'lock_held':
        // The server refused (never applied) a foreign-locked edit — rolling
        // it back leaves local == server for that section. Newer independent
        // ops re-apply below.
        onOpDropped(op, { reason: 'lock_held', holder: frame.holder });
        break;
      case 'stale': {
        // A structural op rejected stale must not be locally re-derived (array
        // re-ordering hazard) — resync to the authoritative order. Non-structural
        // (replace) ops re-derive their path by instanceId and resubmit.
        if (op.structural) {
          onResyncNeeded('structural-stale');
          return; // resync rebuilds the outbox; skip survivor re-apply
        }
        const localBeforeRejected = rewindOptimisticState(preRollback, rejectedAndNewer);
        resubmitStale(op, localBeforeRejected);
        break;
      }
      default:
        // apply_failed / not_seeded / rate_limited / too_large / unknown are
        // all transient or unrecoverable-in-place: dropping a rejected op while
        // keeping the causally-later ones corrupts structure, so the only safe
        // recovery is a full snapshot resync (adopt the converged room state).
        onResyncNeeded(frame.reason);
        return; // resync rebuilds the outbox; skip survivor re-apply
    }
    // Re-apply survivors (the newer ops) re-derived against converged state.
    // They remain on the wire under their original clientOpIds; a subsequent
    // ack whose id is no longer pending-head trips echo-mismatch → resync
    // (a safe, rare degradation for multi-op rejection cascades).
    if (newer.length) pending = pending.concat(reapply(newer, preRollback));
  }

  function resubmitStale(op, preRollback) {
    if (op.staleTries >= MAX_STALE_RESUBMITS) {
      // Repeatedly rejected against a moving structure — stop retrying and
      // resync rather than drop (a drop would strand the local edit divergent).
      onResyncNeeded('stale-giveup');
      return;
    }
    const rederived = rederivePatchesByInstance(op.forward, preRollback, store.getState());
    if (!rederived) {
      onOpDropped(op, { reason: 'stale-vanished' });
      return;
    }
    const reverse = reverseOf(rederived);
    apply(rederived, { label: op.label, seq: lastAppliedSeq });
    const resubmitted = buildOp(rederived, reverse, op.label, op.staleTries + 1);
    if (!resubmitted) {
      onResyncNeeded('unclassifiable-resubmit');
      return;
    }
    pending.push(resubmitted);
    sendOp(resubmitted);
  }

  // ── catchup / reconnect reconciliation ──
  function applyCatchupOp(op) {
    if (op.seq <= lastAppliedSeq) return;
    if (op.clientOpId) {
      const pendingIndex = pending.findIndex((entry) => entry.clientOpId === op.clientOpId);
      if (pendingIndex >= 0) {
        // The server persisted this optimistic edit before the connection died.
        // It is already present locally, so acknowledge without applying twice.
        pending.splice(pendingIndex, 1);
        lastAppliedSeq = op.seq;
        return;
      }
    }
    applyRemoteFrame({
      ...op,
      authorConnId: op.authorConnId ?? op.authorConn,
      clientOpId: undefined
    });
  }

  function discardAccepted(clientOpIds) {
    if (!Array.isArray(clientOpIds) || !clientOpIds.length) return;
    const accepted = new Set(clientOpIds);
    pending = pending.filter((op) => !accepted.has(op.clientOpId));
  }

  function rebasePendingAfterSnapshot(previousState, seq) {
    lastAppliedSeq = Number.isInteger(seq) ? seq : 0;
    if (!pending.length) return;
    pending = reapply(pending, previousState).map((op) => ({ ...op, baseSeq: lastAppliedSeq }));
  }

  function flushPending() {
    for (const op of pending) sendOp(op);
  }

  return {
    install() {
      if (installed) return;
      installed = true;
      store.setCollabSink((entry) => onLocalOp(entry));
    },
    uninstall() {
      installed = false;
      store.setCollabSink(null);
      pending = [];
    },
    reset(seq = 0) {
      pending = [];
      lastAppliedSeq = Number.isInteger(seq) ? seq : 0;
    },
    beginReplay(seq = 0) {
      lastAppliedSeq = Number.isInteger(seq) ? seq : 0;
    },
    handleServerOp,
    handleRejected,
    applyCatchupOp,
    discardAccepted,
    rebasePendingAfterSnapshot,
    flushPending,
    pendingClientOpIds() {
      return pending.map((op) => op.clientOpId);
    },
    setLastAppliedSeq(seq) {
      if (Number.isInteger(seq)) lastAppliedSeq = seq;
    },
    getLastAppliedSeq() {
      return lastAppliedSeq;
    },
    outboxSize() {
      return pending.length;
    },
    hasPending() {
      return pending.length > 0;
    },
    /** In-session parity: outbox drained AND caught up to the room head. */
    isSynced(serverHead) {
      return pending.length === 0 && (serverHead == null || lastAppliedSeq >= serverHead);
    },
    // Test/inspection seam.
    _pending() {
      return pending;
    }
  };
}
